<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import AppIcon from './AppIcon.vue'
import BluffReveal from './BluffReveal.vue'
import type { PlayerBluffView } from '../../shared/contracts/bluff'
import { optionLetter, secondsLeft, useClock } from '../services/bluff'

const props = defineProps<{
  bluff: PlayerBluffView
  prompt: string
  closeAt: string | null
  busy: boolean
  standings: Array<{ id: string; name: string; score: number }>
}>()
const emit = defineEmits<{ bluff: [text: string]; vote: [optionId: string] }>()

const text = ref('')
const now = useClock()
const remaining = computed(() => secondsLeft(props.closeAt, now.value))
const canBluff = computed(() => props.bluff.isCaptain && props.bluff.eligible && props.bluff.myBluff?.status !== 'ACCEPTED' && remaining.value !== 0)
const canVote = computed(() => props.bluff.isCaptain && props.bluff.eligible && !props.bluff.myVoteOptionId && !props.bluff.votingClosed && remaining.value !== 0)

// A new question, or a rejected bluff, must start with an empty box.
watch([() => props.bluff.questionNumber, () => props.bluff.myBluff?.status], () => { text.value = '' })
</script>

<template>
  <div class="stack stack--lg">
    <div class="row-flex row-flex--between">
      <span class="tag">Question {{ bluff.questionNumber }} of {{ bluff.questionCount }}</span>
      <span
        v-if="remaining !== null && ['BLUFF_OPEN', 'VOTE_OPEN'].includes(bluff.phase)"
        class="bluff-countdown"
        aria-live="polite"
      ><AppIcon
        name="clock"
        :size="18"
      />{{ remaining }}s</span>
    </div>
    <h2 class="fw-6">
      {{ prompt }}
    </h2>

    <p
      v-if="!bluff.eligible && ['BLUFF_OPEN', 'REVIEW', 'VOTE_OPEN'].includes(bluff.phase)"
      class="notice"
    >
      Your team joined after this question opened. You're in from the next one.
    </p>

    <!-- bluffing -->
    <template v-else-if="bluff.phase === 'BLUFF_OPEN'">
      <p
        v-if="bluff.myBluff?.status === 'ACCEPTED'"
        class="answer-locked"
      >
        Your team's bluff: <strong>{{ bluff.myBluff.text }}</strong>
      </p>
      <p
        v-if="bluff.myBluff?.status === 'REJECTED'"
        class="alert"
        role="status"
      >
        The host couldn't use “{{ bluff.myBluff.text }}”. {{ bluff.isCaptain ? 'Write another bluff.' : 'Your captain can write another.' }}
      </p>
      <form
        v-if="canBluff"
        class="stack"
        @submit.prevent="emit('bluff', text)"
      >
        <div class="input-wrapper">
          <label for="bluff">Your team's bluff</label>
          <div class="input-single">
            <AppIcon name="message" />
            <input
              id="bluff"
              v-model="text"
              maxlength="80"
              autocomplete="off"
              placeholder="Make up a believable answer"
              required
            >
          </div>
        </div>
        <button
          type="submit"
          class="btn--primary btn--block"
          :disabled="busy || !text.trim()"
        >
          <AppIcon name="lock" /> Lock bluff
        </button>
      </form>
      <p
        v-else-if="!bluff.isCaptain && !bluff.myBluff"
        class="muted-text"
      >
        Your captain is writing the team's bluff. Talk it over. The best lies sound boring.
      </p>
      <p
        v-else-if="remaining === 0 && !bluff.myBluff"
        class="muted-text"
      >
        Time's up for bluffs.
      </p>
    </template>

    <!-- review -->
    <template v-else-if="bluff.phase === 'REVIEW'">
      <p
        v-if="bluff.myBluff?.status === 'ACCEPTED'"
        class="answer-locked"
      >
        Your team's bluff: <strong>{{ bluff.myBluff.text }}</strong>
      </p>
      <p class="muted-text">
        Bluffs are in. The host is mixing them with the truth…
      </p>
    </template>

    <!-- voting -->
    <template v-else-if="bluff.phase === 'VOTE_OPEN'">
      <p class="fw-6">
        {{ bluff.myVoteOptionId ? 'Vote locked. Fingers crossed.' : bluff.votingClosed || remaining === 0 ? 'Voting is closed.'
          : bluff.isCaptain ? 'Which answer is the truth?' : 'Your captain is voting. Which one is true?' }}
      </p>
      <ul class="option-list">
        <li
          v-for="(option, index) in bluff.options"
          :key="option.id"
        >
          <button
            type="button"
            class="option-button"
            :class="{ 'is-chosen': option.id === bluff.myVoteOptionId, 'is-mine': option.mine }"
            :disabled="busy || !canVote || option.mine"
            :aria-pressed="option.id === bluff.myVoteOptionId"
            @click="emit('vote', option.id)"
          >
            <span class="option-letter">{{ optionLetter(index) }}</span>
            <span>{{ option.text }}</span>
            <small v-if="option.mine">your bluff</small>
            <small v-else-if="option.id === bluff.myVoteOptionId">your vote</small>
          </button>
        </li>
      </ul>
    </template>

    <!-- reveal and standings -->
    <template v-else-if="['REVEAL', 'STANDINGS', 'COMPLETE'].includes(bluff.phase)">
      <template v-if="bluff.reveal">
        <p
          v-if="bluff.pointsEarned !== null"
          class="text-xl"
        >
          <strong class="secondary-text">{{ bluff.pointsEarned }}</strong> points this question
        </p>
        <BluffReveal :reveal="bluff.reveal" />
      </template>
      <p
        v-else
        class="muted-text"
      >
        This question was skipped.
      </p>
      <h2 class="fw-6">
        {{ bluff.phase === 'COMPLETE' ? 'Final standings' : 'Standings' }}
      </h2>
      <ol class="standings-list">
        <li
          v-for="(team, index) in standings"
          :key="team.id"
        >
          <strong><span
            class="avatar"
            :class="{ 'avatar--secondary': index === 0 }"
          >{{ index + 1 }}</span>{{ team.name }}</strong><span>{{ team.score }} points</span>
        </li>
      </ol>
    </template>
  </div>
</template>
